import React, { useState } from 'react';
import { SignIn, SignUp } from '@clerk/react';
import LoginSplashScreen from './LoginSplashScreen';
import BetaKeyGate from './BetaKeyGate';
import { clearAdmissionToken } from '../../auth/betaAdmissionToken';
import { hasPendingInvite, clearStoredInvite } from '../../auth/inviteKey';

/**
 * Clerk-lane sign-in entry point.
 *
 * Two calls to action on the splash art, each opening a popup:
 *
 * - **Sign In** -> Clerk's `<SignIn>`. The email/password fields AND the "continue
 *   with Google / other provider" buttons both come from Clerk — the providers are
 *   configured in the Clerk dashboard, not in this repo, so this screen must not
 *   reimplement or hand-roll them.
 *
 * - **Sign Up** -> `BetaKeyGate` FIRST, then Clerk's `<SignUp>`. Mythic is in closed
 *   beta, so the invite is checked before we let anyone create a Clerk identity.
 *
 * ============================================================================
 * WHY THE BETA KEY IS CHECKED BEFORE CLERK, NOT AFTER
 * ============================================================================
 *
 * The backend's admission point is the bridge: a new Clerk identity with no Django
 * user gets `403 beta_key_required`, and `ClerkBridgeCoordinator` renders
 * `ClerkBetaAdmissionScreen`. That safety net still exists and is still
 * authoritative — nothing here replaces it.
 *
 * But relying on it ALONE meant a visitor with no invite could complete a full
 * Clerk signup (verifying an email, setting a password, maybe linking Google) only
 * to be told at the very end that they were never admitted — leaving an orphaned
 * Clerk identity with no Django account behind it. Asking for the key first fails
 * that visitor in one step instead of five, and leaves no orphan.
 *
 * This pre-check is SAFE because `validateBetaKey()` is read-only: it does not call
 * `use_key()`, so it cannot decrement a shared key's limited `usage_count`. The key
 * is still spent exactly once, server-side under lock, at the bridge.
 *
 * It is a COURTESY CHECK, NOT AN AUTHORIZATION DECISION. The backend re-validates at
 * the bridge and may still reject — for example if the key was exhausted by someone
 * else between this check and the bridge call. That is correct and expected; the
 * frontend must not treat passing this gate as admission granted.
 *
 * It must NEVER route to the legacy `BetaKeyValidation -> UserRegistration` flow.
 * That is the pre-Clerk registration path; sending a Clerk user there would create a
 * second, unmapped Django account. The two lanes are never both live —
 * `ClerkStatusBootstrap` picks exactly one.
 */
/**
 * @param onBetaKeyValidated Optional. Called with the validated key so the bridge
 *   coordinator (which outlives this screen) can carry it into the first bridge
 *   attempt, sparing an in-page signup a second key prompt. Optional because this
 *   screen is also rendered standalone in the component viewer.
 */
function ClerkAuthenticationScreen({ onBetaKeyValidated }) {
    /*
     * `null` = splash only.
     * 'signIn'   = Clerk sign-in.
     * 'betaGate' = closed-beta key check (step 1 of sign-up).
     * 'signUp'   = Clerk sign-up (step 2, only reachable via 'betaGate').
     *
     * ========================================================================
     * A QR / INVITE-LINK VISITOR OPENS ON THE BETA GATE, NOT THE SPLASH
     * ========================================================================
     *
     * Someone who scanned `/signup#invite=<KEY>` has already ASKED to redeem a
     * key. Starting them on the generic Sign In / Sign Up splash made the QR
     * code worthless: they pressed Sign Up and found an empty beta-key box,
     * because the splash gives no hint that an invite was ever captured.
     *
     * Read from STORAGE, not the URL: `captureInviteKeyFromUrl()` (AppRouter,
     * module load) has already stripped the fragment by the time this renders,
     * and storage is also what survives the Clerk/OAuth round trip.
     *
     * `hasPendingInvite()` — NOT `getStoredInviteKey()` — is deliberate. A link
     * that could not be parsed stores an ERROR MARKER and no key; a key-only
     * check would drop exactly those visitors on the splash, where the "that
     * link didn't look right" notice can never render and the failure is
     * silent. Anyone who arrived via a link must be told what happened to it.
     *
     * Lazy initializer so this is evaluated ONCE per mount. A plain call would
     * re-read storage on every render and could yank a user who has moved on
     * back to the gate.
     */
    const [panel, setPanel] = useState(() => (hasPendingInvite() ? 'betaGate' : null));

    /*
     * Whether the beta gate has been cleared in this attempt, held IN MEMORY ONLY.
     *
     * Its ONLY job is to enforce ordering — it is the evidence `renderPanel()` checks
     * before it will mount Clerk sign-up. It is deliberately NOT persisted: per the
     * storage rules, only a URL-derived SHARED invite may live in sessionStorage, and
     * `inviteKey.js` already owns that case.
     *
     * This flag does NOT carry the key to the bridge's admission step, and it must
     * not be made to. The key travels in memory (`onBetaKeyValidated` -> the
     * coordinator) for an in-page signup, and the backend's short-lived ADMISSION
     * TOKEN — captured by `BetaKeyGate`, see `betaAdmissionToken.js` — is what
     * survives an OAuth redirect. The typed key itself is still never persisted.
     */
    const [betaGateCleared, setBetaGateCleared] = useState(false);

    const closePanel = () => {
        setPanel(null);
        // Abandoning sign-up must revoke the cleared-gate evidence, or reopening
        // Sign Up later in this tab would skip the beta check.
        setBetaGateCleared(false);
        // The same abandonment must drop any admission token minted during the
        // attempt. It is single-use and would otherwise still be attached the next
        // time someone signs up in this tab — spending, or failing against, a
        // credential that attempt never presented.
        clearAdmissionToken();

        /*
         * CLOSING THE POPUP ABANDONS A PENDING INVITE — otherwise it is a trap.
         *
         * `panel` is initialized from `hasPendingInvite()`, so as long as the
         * invite sits in storage this screen reopens on the beta gate every time
         * it mounts. Without this clear, a user who deliberately closed the gate
         * would be dumped straight back into it by the next remount (the bridge
         * coordinator remounts this screen on Clerk load transitions), with no
         * way out short of clearing site data.
         *
         * Nothing of value is lost: pre-validation does NOT consume the key
         * (`usage_count` is only incremented server-side at the bridge), and the
         * link can simply be opened again.
         *
         * This mirrors the legacy lane's `handleBackToAuth`/`handleBackToRealLogin`
         * in AppRouter, which clear the invite for exactly the same reason.
         */
        clearStoredInvite();
    };

    const handleBetaKeyValidated = (betaKey) => {
        if (typeof betaKey !== 'string' || !betaKey.trim()) {
            throw new Error(
                `BetaKeyGate resolved without a usable key; received ${typeof betaKey}.`
            );
        }
        // Hand the key up to the coordinator BEFORE Clerk sign-up opens. This screen
        // unmounts the instant Clerk signs in, so a key kept only in local state
        // would vanish exactly when the bridge needs it — that is what caused the
        // duplicate prompt. The coordinator survives the transition.
        if (typeof onBetaKeyValidated === 'function') {
            onBetaKeyValidated(betaKey);
        }
        setBetaGateCleared(true);
        setPanel('signUp');
    };

    /**
     * Build the open panel, and ONLY the open panel.
     *
     * Deliberately a function rather than a computed value: this must not construct
     * a Clerk element while the popup is closed, or while the beta gate is showing.
     * Clerk's components are heavyweight and stateful, and mounting `<SignUp>` early
     * would start a Clerk flow before the user has cleared the beta check — exactly
     * what this ordering exists to prevent.
     *
     * `routing="virtual"` on both Clerk panels: this app has a state-based router
     * (`AppRouter.js`), not a host router capable of owning Clerk's
     * factor/verification/reset sub-paths. Virtual routing keeps the whole multi-step
     * Clerk flow inside this React tree, which is also what lets it live in a popup —
     * a path-routed widget would navigate the document out from under the modal.
     */
    const renderPanel = () => {
        if (panel === 'signIn') {
            /*
             * `withSignUp={false}` and `transferable={false}` KEEP THE BETA GATE
             * CLOSED. Both are load-bearing; neither is cosmetic.
             *
             * Clerk's <SignIn> is a sign-in-OR-UP widget by default, and
             * `transferable` defaults to TRUE — meaning an OAuth attempt with an
             * email Clerk has never seen is silently "transferred" into a signup
             * (Clerk calls these opaque sign-ups). Left at the defaults, pressing
             * **Sign In -> Google** with a brand-new address creates a Clerk
             * identity without ever passing `BetaKeyGate`, recreating exactly the
             * orphaned-Clerk-account problem the gate exists to prevent.
             *
             * `withSignUp={false}` also removes the widget's internal "Sign up"
             * link, so the only route to account creation is the splash's Sign Up
             * button -> beta gate.
             *
             * NOTE: this is NOT an authorization hole either way — the backend
             * bridge still refuses to provision a Django user without a valid key.
             * The damage is a stranded Clerk identity with no account behind it, and
             * a user who thinks they signed up but cannot get in.
             *
             * These two props CANNOT be verified by the unit tests below: they mock
             * <SignIn> with a stub, so the assertions only prove the props are
             * passed, never that Clerk honours them. Real coverage is the browser
             * step in the manual plan (Sign In -> Google with an unknown email).
             */
            return <SignIn routing="virtual" withSignUp={false} transferable={false} />;
        }
        if (panel === 'betaGate') {
            return <BetaKeyGate onValidated={handleBetaKeyValidated} />;
        }
        if (panel === 'signUp') {
            // Guard the ordering rather than trusting it. Reaching Clerk sign-up
            // without a cleared gate means the check was bypassed by a future edit,
            // and silently rendering signup would reopen the orphaned-identity hole.
            if (!betaGateCleared) {
                throw new Error(
                    'Clerk sign-up was opened without a validated beta key; the ' +
                    'closed-beta gate must run first.'
                );
            }
            return <SignUp routing="virtual" />;
        }
        throw new Error(`Unknown Clerk auth panel: ${String(panel)}`);
    };

    // Clerk's own cards carry their own headings, so a title is supplied only for
    // the beta gate — otherwise the popup would render a duplicate heading. This
    // heading is now the gate's ONLY instruction text, so it must not be removed.
    const popupTitle = panel === 'betaGate' ? 'Enter Your Beta Key' : '';

    const panelLabel = {
        signIn: 'Clerk sign in',
        betaGate: 'Beta key required',
        signUp: 'Clerk sign up'
    }[panel];

    return (
        <LoginSplashScreen
            onSignIn={() => setPanel('signIn')}
            onSignUp={() => setPanel('betaGate')}
            isPopupOpen={panel !== null}
            onPopupClose={closePanel}
            popupTitle={popupTitle}
            popupSize="sm"
            popup={
                panel === null ? null : (
                    <div
                        // Clerk's cards center themselves inside a flex wrapper; the
                        // beta-gate form is our own and must fill the popup width
                        // instead of being squeezed to its content.
                        className={panel === 'betaGate' ? undefined : 'login-splash__clerk'}
                        aria-label={panelLabel}
                    >
                        {renderPanel()}
                    </div>
                )
            }
        />
    );
}

export default ClerkAuthenticationScreen;
